import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateMachines } from "@/hooks/use-machines";
import { api } from "@/lib/api";
import { TOAST_ERROR_DURATION_MS, TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Invalidate every view a cluster production change can affect. */
function invalidateClusterAndMachines(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["k8s-clusters"] });
  queryClient.invalidateQueries({ queryKey: ["k8s-cluster-machines"] });
  // Dropped/re-added member nodes affect the all-machines + CBG views; reuse the
  // canonical machine-invalidation set (both keys, per the frontend pitfall doc).
  invalidateMachines(queryClient);
}

export function useK8sClusters() {
  return useQuery({
    queryKey: ["k8s-clusters"],
    queryFn: () => api.listK8sClusters(),
  });
}

export function useK8sClusterMachines(clusterId: number) {
  return useQuery({
    queryKey: ["k8s-cluster-machines", clusterId],
    queryFn: () => api.listK8sClusterMachines(clusterId),
    enabled: clusterId > 0,
  });
}

export function useAcquireK8sCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: number) => api.acquireK8sCluster(clusterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["k8s-clusters"] });
    },
  });
}

export function useReleaseK8sCluster() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: number) => api.releaseK8sCluster(clusterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["k8s-clusters"] });
    },
  });
}

export function useMarkK8sClusterProduction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: number) => api.markK8sClusterProduction(clusterId),
    onSuccess: (cluster) => {
      invalidateClusterAndMachines(queryClient);
      toast.success(`Marked ${cluster.name} as production`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error) => {
      toast.error("Failed to mark cluster production", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}

export function useUnmarkK8sClusterProduction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clusterId: number) => api.unmarkK8sClusterProduction(clusterId),
    onSuccess: (cluster) => {
      invalidateClusterAndMachines(queryClient);
      toast.success(`${cluster.name} returned to the pool`, {
        duration: TOAST_SUCCESS_DURATION_MS,
      });
    },
    onError: (error) => {
      toast.error("Failed to unmark cluster", {
        description: errorMessage(error),
        duration: TOAST_ERROR_DURATION_MS,
      });
    },
  });
}
